import { useState } from "react";

import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { sourceDescription, sourceLabel } from "@/modules/landing_pages/stats-format";

import type { TtuCatalog } from "../api";
import { PickerShell } from "./ProductPicker";

const formatSessions = (value: number) => new Intl.NumberFormat("en-GB").format(value);
const AMAZON_DESCRIPTION: Record<string, string> = {
  sponsored: "Amazon sponsored ads: Sponsored Products clicks and the orders Amazon attributes to them",
  not_attributed: "Amazon not attributed: every other Amazon session, including Sponsored Brands and Display clicks, which Amazon reports per campaign and not per product",
};

export function sourceCaption(value: string | null): string {
  if (value === null) return "All sources";
  if (value === "sponsored") return "Amazon sponsored ads";
  if (value === "not_attributed") return "Amazon not attributed";
  return sourceLabel(value);
}

export function sourceSearchValue(label: string, key: string): string {
  return `${label} ${key}`;
}

export function SourcePicker({ sources, value, onChange, disabled = false }: {
  sources: TtuCatalog["sources"];
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const pick = (next: string | null) => { onChange(next); setOpen(false); };
  return <PickerShell label="Traffic source" caption={sourceCaption(value)} open={open} setOpen={setOpen} disabled={disabled}>
    <SourcePickerOptions sources={sources} onChange={pick} />
  </PickerShell>;
}

export function SourcePickerOptions({ sources, onChange }: {
  sources: TtuCatalog["sources"];
  onChange: (value: string | null) => void;
}) {
  const render = (source: TtuCatalog["sources"][number]) => {
    const label = sourceCaption(source.key);
    const description = source.channel === "Shopify" ? sourceDescription(source.key) : AMAZON_DESCRIPTION[source.key];
    return <CommandItem data-nonmutating key={`${source.channel}:${source.key}`} value={sourceSearchValue(label, source.key)} onSelect={() => onChange(source.key)}>
      <span className="flex min-w-0 flex-1 flex-col"><span>{label} · {formatSessions(source.sessions)} sessions</span>{description && <span className="text-xs text-muted-foreground">{description}</span>}</span>
    </CommandItem>;
  };
  return <Command><CommandInput data-nonmutating placeholder="Search sources" /><CommandList>
      <CommandEmpty>No matching source.</CommandEmpty>
      <CommandItem data-nonmutating value="__all__ All sources" onSelect={() => onChange(null)}>
        <span className="flex min-w-0 flex-1 flex-col"><span>All sources</span><span className="text-xs text-muted-foreground">Every session and order, whatever brought it.</span></span>
      </CommandItem>
      <CommandGroup heading="Storefront (Shopify), as in Funnels">{sources.filter((source) => source.channel === "Shopify").map(render)}</CommandGroup>
      <CommandGroup heading="Amazon">{sources.filter((source) => source.channel === "Amazon").map(render)}</CommandGroup>
    </CommandList></Command>;
}
